import { useDateFormat } from "@kaltura/mediaspace-shared-hooks";
import { ChevronRight16Icon } from "@kaltura/ds-react-icons";
import { KmsTypeSchedulingTimeInterval } from "@kaltura/mediaspace-shared-types";
import styled from "@emotion/styled";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Badge, TruncatedLine } from "@kaltura/mediaspace-shared-ui";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";

export interface ScheduleProps {
    schedulingData: KmsTypeSchedulingTimeInterval;
    className?: string;
}

const StyledSchedule = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(0.5),
    "& .kms-ds-mediacard-scheduleline": {
        alignItems: "center",
        ...theme.kaltura.typography.body2,
        color: theme.kaltura.palette.tone2,
    },
}));

const StyledChevronRight16Icon = styled(ChevronRight16Icon)({
    verticalAlign: "text-top",
});

/**
 * scheduling info for entries with scheduling
 */
export const Schedule = ({ schedulingData, className }: ScheduleProps) => {
    const {
        application: { useUserTimezone },
    } = useConfig();

    const { start, end } = schedulingData;
    const { startDay, endDay, formattedScheduledStartTime, formattedScheduledEndTime } = useDateFormat(
        start.timestamp * 1000,
        end.timestamp * 1000,
        useUserTimezone ? undefined : start.timeZoneName,
        false
    );

    return (
        <StyledSchedule className={className}>
            <TruncatedLine className={"kms-ds-mediacard-scheduleline"}>
                {startDay === endDay && (
                    <>
                        {startDay},&nbsp;{formattedScheduledStartTime}&nbsp;-&nbsp;
                        {formattedScheduledEndTime}
                    </>
                )}
                {startDay !== endDay && (
                    <>
                        {`${startDay}, ${formattedScheduledStartTime}`}
                        &nbsp;
                        <StyledChevronRight16Icon />
                        &nbsp;
                        {`${endDay}, ${formattedScheduledEndTime}`}
                    </>
                )}
            </TruncatedLine>
            <Badge label={translate("scheduled")} size={"small"} mode={"light"} />
        </StyledSchedule>
    );
};
